import { MatSnackBar } from "@angular/material/snack-bar";
import { Inject } from "@angular/core";
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from "@angular/material/dialog";

import {
  Component,
  DestroyRef,
  OnInit,
  inject,
  signal,
} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { finalize } from "rxjs/operators";

// Services
import { StoreUserService } from '@app/core/state/store-user.service';
import { StoreAuthService } from '@app/core/auth/store-auth.service';
import { ListMembersService } from "../../../data/member/list-members.service";
import { ContributorAccessService } from "../../../data/belongs/contributor-access.service";

import { DatabaseCacheService } from "../../../data/cache/database-cache.service";
import { SHARED_IMPORTS } from "src/app/shared/standalone-imports";

@Component({
  selector: "app-contributor-picker",
  templateUrl: "./contributor-picker.component.html",
  styleUrls: ["./contributor-picker.component.scss"],
  imports: SHARED_IMPORTS,
})
export class ContributorPickerComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  // Loading state for skeleton - true initially to show skeleton until data is ready
  isLoading = signal(true);

  userName: string;
  userId: string;

  contributors: any[] = [];
  filteredContributors: any[] = [];

  selectedContributorId: string;
  filterText = '';
  selectedJobTitles: Set<string> = new Set();

  constructor(
    public dialogRef: MatDialogRef<ContributorPickerComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public _matdialog: MatDialog,
    public storeUser: StoreUserService,
    public storeAuth: StoreAuthService,
    private route: ActivatedRoute,
    public snackBar: MatSnackBar,
    public common: DatabaseCacheService,
    private listMembersService: ListMembersService,
    private contributorAccessService: ContributorAccessService,
  ) {}

  ngOnInit() {
    this.userName = this.data?.userName;
    this.userId = this.data?.userId;
    this.listContributors();
  }

  onAssignClick(): void {
    if (!this.selectedContributorId) return;

    this.contributorAccessService
      .createContributorAccess(this.userId, this.selectedContributorId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (res) => {
          const selectedContributor = this.contributors.find(
            (c) => c.user.userId === this.selectedContributorId,
          );

          if (selectedContributor) {
            const assignedContributor = {
              type: null,
              userId: selectedContributor.user.userId,
              firstName: selectedContributor.user.firstName,
              lastName: selectedContributor.user.lastName,
              jobTitleCode: selectedContributor.attributes.jobTitleCode,
            };
            this.dialogRef.close(assignedContributor);
          }
        },
        (error) => {
          this.snackBar.open('エラーが発生しました', '閉じる', { duration: 3000 });
        },
      );
  }

  onCancelClick(): void {
    this.dialogRef.close();
  }

  selectContributor(userId: string): void {
    this.selectedContributorId = userId;
  }

  isJobTitleSelected(code: string): boolean {
    return this.selectedJobTitles.has(code);
  }

  toggleJobTitleFilter(code: string): void {
    if (this.selectedJobTitles.has(code)) {
      this.selectedJobTitles.delete(code);
    } else {
      this.selectedJobTitles.add(code);
    }
    this.applyFilter2Contributors();
  }

  applyFilter2Contributors(): void {
    let result = [...this.contributors];

    // Filter by selected job titles
    if (this.selectedJobTitles.size > 0) {
      result = result.filter(c =>
        this.selectedJobTitles.has(c.attributes.jobTitleCode)
      );
    }

    // Filter by text
    if (this.filterText.length > 0) {
      const searchText = this.filterText.toLowerCase();
      result = result.filter(c =>
        c.displayText.toLowerCase().includes(searchText)
      );
    }

    this.filteredContributors = result;
  }

  listContributors(): void {
    this.isLoading.set(true);
    this.listMembersService
      .listMembers(
        this.common.getSelectedSection().sectionId,
        "CONTRIBUTOR",
        true, // activeUsersOnly 
        false // includeClosedSections 
      )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isLoading.set(false))
      )
      .subscribe({
        next: (res) => {
          this.contributors = res as any[];
          this.contributors.forEach(
            (c) =>
              (c.displayText =
                c.user.lastName +
                c.user.firstName +
                "(" +
                this.common.getJobTitle(c.attributes.jobTitleCode) +
                ")"),
          );

          this.filteredContributors = [...this.contributors];
        },
        error: () => {
          // finalize handles isLoading.set(false)
        },
      });
  }
}
